import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { DEMO_BILLING_NOTE, useIsDemo } from '../account/beta.ts';
import { entitlementFor, useAccount, walletFor } from '../account/store.ts';
import { apiMessage } from '../api/errors.ts';
import { api } from '../api/instance.ts';
import type { Org } from '../api/types.ts';
import { BrandingEditor } from '../branding/BrandingEditor.tsx';
import { buyCreditPack, checkoutTeam, openBillingPortal } from '../plans/billing.ts';
import { formatAmount, has, planName } from '../plans/features.ts';
import { useFlags } from '../plans/gate.ts';
import { loadPlans, usePlans } from '../plans/store.ts';
import { services } from '../services/index.ts';
import { toast } from '../state/feedback.ts';
import { Button } from '../ui/Button.tsx';
import { Field } from '../ui/Field.tsx';
import { IconButton } from '../ui/IconButton.tsx';
import { Section } from '../ui/Section.tsx';
import { Segmented } from '../ui/Segmented.tsx';
import { Members } from './Members.tsx';
import { PriceList } from './PriceList.tsx';
import { canEditCatalog, isAdmin, ROLE_LABELS, type Role } from './roles.ts';
import { Usage } from './Usage.tsx';

type Tab = 'members' | 'prices' | 'branding' | 'usage' | 'billing';
const TABS: Tab[] = ['members', 'prices', 'branding', 'usage', 'billing'];

function Billing({ org, role }: { org: Org; role: Role }) {
  const me = useAccount((a) => a.me);
  const catalog = usePlans((p) => p.catalog);
  const ent = entitlementFor(me, org.id);
  const wallet = walletFor(me, org.id);
  const [seats, setSeats] = useState(String(Math.max(2, org.seats)));
  const demo = useIsDemo();
  const [interval, setBilling] = useState<'month' | 'year'>('year');
  const team = ent?.plan === 'team';
  useEffect(() => {
    void loadPlans();
  }, []);
  return (
    <div className="flex flex-col gap-6">
      <Section title="Plan">
        <p className="t-body">
          {planName(ent?.plan ?? 'free', catalog)}
          {team ? ` · ${org.seats} seats` : ''}
          {ent?.trialEndsAt ? ` · trial until ${new Date(ent.trialEndsAt).toLocaleDateString()}` : ''}
          {wallet
            ? wallet.memberAllowance != null
              ? ` · ${wallet.memberAllowance} AI credits per member each month${wallet.purchased ? `, ${wallet.purchased} shared pack credits` : ''}`
              : ` · ${wallet.balance} AI credits`
            : ''}
        </p>
        {demo ? (
          <p className="t-caption text-dim" data-testid="demo-note">
            {DEMO_BILLING_NOTE}
          </p>
        ) : null}
        {team && role === 'owner' && !demo ? (
          <Button className="self-start" onClick={() => void openBillingPortal(org.id)}>
            Manage billing and seats
          </Button>
        ) : null}
        {!team && isAdmin(role) ? (
          <div className="flex flex-wrap items-end gap-2">
            <Field
              label="Seats"
              className="w-28"
              inputMode="numeric"
              value={seats}
              onChange={(e) => setSeats(e.target.value.replace(/\D/g, ''))}
            />
            <Segmented
              label="Billing interval"
              size="sm"
              className="w-[200px]"
              value={interval}
              onChange={setBilling}
              options={[
                { value: 'month', label: 'Monthly' },
                { value: 'year', label: 'Yearly' },
              ]}
            />
            <Button
              variant="primary"
              disabled={demo}
              onClick={() => void checkoutTeam(org.id, Number.parseInt(seats, 10) || 2, interval)}
            >
              Start Team plan
            </Button>
          </div>
        ) : null}
      </Section>
      {isAdmin(role) && catalog?.creditPacks.length ? (
        <Section title="Credits for the company">
          <div className="flex flex-wrap gap-2">
            {catalog.creditPacks.map((p) => (
              <Button key={p.id} size="sm" disabled={demo} onClick={() => void buyCreditPack(p.id, org.id)}>
                {p.credits} credits · {formatAmount(p.amount, catalog.currency)}
              </Button>
            ))}
          </div>
        </Section>
      ) : null}
    </div>
  );
}

/** Company admin (docs/spec/08 §4): members, invites, roles, price list, branding, usage, billing. */
export function TeamScreen() {
  const { orgId } = useParams({ from: '/team/$orgId' });
  const search = useSearch({ from: '/team/$orgId' });
  const navigate = useNavigate();
  const { session, me, ready } = useAccount(useShallow((s) => ({ session: s.session, me: s.me, ready: s.ready })));
  const [org, setOrg] = useState<Org | null>(null);
  const [tab, setTab] = useState<Tab>('members');
  // ?tab= deep links (e.g. the Team paywall → billing), also while the screen is already open.
  useEffect(() => {
    if (search.tab && TABS.includes(search.tab as Tab)) setTab(search.tab as Tab);
  }, [search.tab]);
  const flags = useFlags(orgId);

  useEffect(() => {
    if (ready && !session) {
      void navigate({ to: '/signin', search: { next: `/team/${orgId}` }, replace: true });
      return;
    }
    if (!session) return;
    if (search.checkout === 'success') {
      toast('Thanks! Your Team plan is being set up.', 'success');
      void services.account.refreshMe();
    }
    void api
      .org(orgId)
      .then(setOrg)
      .catch((e) => toast(apiMessage(e, 'Could not open this company.'), 'error'));
  }, [orgId, ready, session, navigate, search.checkout]);

  const role = (org?.role ?? me?.orgs.find((o) => o.id === orgId)?.role ?? 'viewer') as Role;
  const admin = isAdmin(role);
  const tabs: { value: Tab; label: string }[] = [
    { value: 'members', label: 'Members' },
    { value: 'prices', label: 'Price list' },
    { value: 'branding', label: 'Branding' },
    ...(admin ? [{ value: 'usage' as Tab, label: 'Usage' }] : []),
    { value: 'billing', label: 'Billing' },
  ];

  return (
    <main
      className="min-h-dvh"
      style={{ paddingTop: 'var(--safe-top)', paddingBottom: 'calc(var(--safe-bottom) + 32px)' }}
    >
      <div className="flex min-h-11 items-center gap-2 px-2">
        <IconButton icon={ArrowLeft} label="Back" onClick={() => void navigate({ to: '/account' })} />
        <h1 className="t-title truncate">{org?.name ?? 'Company'}</h1>
        {org ? <span className="t-caption rounded-full bg-surface3 px-2 py-0.5">{ROLE_LABELS[role]}</span> : null}
      </div>
      <div className="mx-auto flex max-w-[860px] flex-col gap-6 p-4">
        <Segmented label="Company sections" size="sm" value={tab} onChange={setTab} options={tabs} />
        {!org || !me ? (
          <p className="t-body text-dim">Loading…</p>
        ) : tab === 'members' ? (
          <Members orgId={orgId} me={me.user.id} myRole={role} seats={org.seats} />
        ) : tab === 'prices' ? (
          <PriceList orgId={orgId} canEdit={canEditCatalog(role)} />
        ) : tab === 'branding' ? (
          <>
            {!has(flags, 'company_branding') ? (
              <p className="t-caption text-accent">
                Company branding is part of the Team plan. Reps use it on every quote.
              </p>
            ) : null}
            <BrandingEditor
              scope={orgId}
              canEdit={admin && has(flags, 'company_branding')}
              templates={has(flags, 'quote_templates')}
            />
          </>
        ) : tab === 'usage' ? (
          <Usage orgId={orgId} canEdit={admin} cap={org.dailyCreditCap} />
        ) : (
          <Billing org={org} role={role} />
        )}
      </div>
    </main>
  );
}
